import { UserX } from 'lucide-react';
import { useAuth } from '../../auth/useAuth';
import { restoreAdminUser, type AdminArchivedUserRow } from '../../lib/apiClient';
import { ConfirmAction } from './ConfirmAction';

/**
 * **Archived Users** (spec §10).
 *
 * Production's second table, and a smaller one: Name, Email, Revenue,
 * Archived, and a Restore control. Sorted by when each account was archived,
 * most recent first.
 *
 * **Restore** is the one action, and it is the same two-step inline
 * confirmation every other control on this console uses.
 *
 * **The section is drawn only when something is archived.** Production wraps it
 * in `deletedUsers.length > 0`, so an empty archive shows no section at all
 * rather than an empty table. The console owns that condition.
 */
export function ArchivedUsersTable({
  users,
  onChanged,
}: {
  users: AdminArchivedUserRow[];
  onChanged: () => void;
}) {
  const { status, session } = useAuth();
  const token = status === 'signed-in' ? (session?.access_token ?? null) : null;

  return (
    <section className="flex flex-col gap-4" data-testid="admin-archived-users">
      {/*
        Production tints this section apart from the rest of the console — a
        destructive-toned heading behind a UserX — because it is the one table
        listing accounts that are no longer live.
      */}
      <div className="flex items-center gap-3">
        <UserX className="h-5 w-5 shrink-0 text-destructive" aria-hidden="true" />
        <div className="flex flex-col gap-0.5">
          <h2 className="text-xl font-bold text-destructive/80">
            Archived Users
          </h2>
          <p className="text-sm text-muted-foreground" data-testid="archived-users-summary">
            {`${users.length} archived. Data is retained and can be restored.`}
          </p>
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-border bg-card">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-border text-sm font-medium text-muted-foreground">
            <tr>
              <th scope="col" className="px-4 py-3">
                Name
              </th>
              <th scope="col" className="px-4 py-3">
                Email
              </th>
              <th scope="col" className="px-4 py-3 text-right">
                Revenue
              </th>
              <th scope="col" className="px-4 py-3">
                Archived
              </th>
              {/* The action column. Production gives it no header text. */}
              <th scope="col" className="w-28 px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {users.map((user) => (
              <tr key={user.id} data-testid="archived-user-row" data-user-id={user.id}>
                <td className="px-4 py-3">
                  {name(user) ? (
                    <span className="font-medium">{name(user)}</span>
                  ) : (
                    <span className="italic text-muted-foreground">no name</span>
                  )}
                </td>

                <td className="px-4 py-3" data-testid="archived-user-email">
                  {user.email ?? <span className="italic text-muted-foreground">no email</span>}
                </td>

                <td className="px-4 py-3 text-right" data-testid="archived-user-revenue">
                  <span
                    className={
                      user.revenueCents > 0
                        ? 'tabular-nums text-green-500'
                        : 'tabular-nums text-muted-foreground'
                    }
                  >
                    {money(user.revenueCents)}
                  </span>
                </td>

                <td className="px-4 py-3" data-testid="archived-user-archived">
                  {user.archivedAt ? localeDate(user.archivedAt) : null}
                </td>

                <td className="w-28 px-4 py-3">
                  <ConfirmAction
                    testId="action-restore"
                    label="Restore"
                    pendingLabel="Restoring..."
                    onConfirm={async () =>
                      token !== null && (await restoreAdminUser(token, user.id))
                    }
                    onChanged={onChanged}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

const name = (user: AdminArchivedUserRow): string =>
  [user.firstName, user.lastName].filter(Boolean).join(' ').trim();

function money(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function localeDate(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString();
}
